import { SbEditableBlok } from '@storyblok/storyblok-editable'
import { Richtext } from 'storyblok-js-client'
import { IconName } from './lib/constants'

export type NavigationBlok = {
  content: {
    items: NavigationItemBlok[]
  }
} & SbEditableBlok

export type SbLink = {
  cached_url: string
  fieldtype: 'story' | 'url' | 'multilink'
  id: string
  linktype: 'story' | 'url' | 'asset' | 'email'
  url: string
  anchor: string
}

export type SbAsset = {
  alt: string
  copyright: string
  fieldtype: 'asset'
  filename: string
  focus: string
  id: number
  is_external_url?: boolean
  name: string
  title: string
  source: string
}

export interface ButtonBlok extends SbEditableBlok {
  button_name: string
  button_style: 'primary' | 'secondary' | 'tertiary' | 'red' | 'green' | 'blue' | 'yellow' | 'link'
  component: 'Button'
  iconLeft?: IconName
  iconRight?: IconName
  ignoreTheme?: boolean
  link_target: boolean
  link_title: string
  link: SbLink
  size?: 'default' | 'small'
  inverted?: boolean

  // analytics
  analytics: boolean // determines if the button should be tracked or not
  googleAnalyticsEventName?: string
  analyticsEventName?: string // ph event name
  analyticsMetadata?: string // ph event metadata
}

export type NavigationItemBlok = {
  component: 'NavigationItem'
  label: string
  link: SbLink
} & SbEditableBlok

export interface TextBlok extends SbEditableBlok {
  component: 'Text'
  text: string
}

export interface TeaserCardBlok extends SbEditableBlok {
  image: SbAsset
  title: string
  description: Richtext
  link?: SbLink
  show_link_arrow_only: boolean
  direction: string
  buttonLabel?: string
  component: 'TeaserCardItem'
}

export interface TeaserCardStepBlok extends SbEditableBlok {
  image: SbAsset
  title: string
  description: Richtext
  link?: SbLink
  show_link_arrow_only: boolean
  direction: string
  buttonLabel?: string
  component: 'TeaserCardStepItem'
}

export interface TeaserProductCardBlok extends SbEditableBlok {
  select_product: string
  component: 'TeaserCardProductItem'
}

export interface LinkBlok extends SbEditableBlok {
  link: SbLink
  text: string
}

export interface ProductFieldBlok {
  field_component: 'Product'
  uuid: string
  lang: string
  name: string
  content?: ProductBlok
  full_slug: string
}

export interface SubnavigationBlok extends SbEditableBlok {
  pageLinks?: Array<SbEditableBlok & { text: string; link: SbLink; icon: string }>
  callToAction?: Array<SbEditableBlok & { text: string; link: SbLink }>
}

export interface ProductBlok extends SbEditableBlok {
  buttons: ButtonBlok[] // Todo: write typings for ButtonBlok
  headline: string
  image: SbAsset
  colorTheme?: string
  component: 'Product'
  contentBlocks: any[]
  description: Richtext
  operationsSystems: OperationSystemBlok[] // Todo: add typings for operation_systems
  productIcon: SbAsset
  productName: string
  subnavigation?: SubnavigationBlok[]
  productVariant: ProductVariant[] // Todo: add typings for product_variant
  shortDescription: string
  technologies: string[] // Todo: add typings for technologies
  variantLink?: Array<{ text: string; link: SbLink }>
  isDropdown?: boolean
}

export type OperationSystemBlok = {
  content: {
    name: string
    icon: string
  }
} & SbEditableBlok

export type TechnologyBlok = {
  content: {
    name: string
    icon: string
  }
} & SbEditableBlok

export interface SampleMetadataCategoriesUseCaseSample {
  Interface: string
  Platforms: string[]
  CodeSnippets: string[]
}

export interface SampleMetadataCategoryUseCase {
  ShortName: string
  MainUseCase: boolean
  TitleEN: string
  DescriptionEN: string
  TitleDE: string
  DescriptionDE: string
  TitleFR: string
  DescriptionFR: string
  Samples: SampleMetadataCategoriesUseCaseSample[]
}

export interface SampleMetadataCategory {
  CategoryEN: string
  CategoryDE: string
  CategoryFR: string
  UseCases: SampleMetadataCategoryUseCase[]
}

export interface SampleMetadataNode {
  id: string
  ProductCode: string
  Categories: SampleMetadataCategory[]
}

export interface RecommendedArticleTeaser {
  uuid: string
  content: RecommendedArticleBlok
  full_slug: string
  group_id: string
  name: string
  lang: string
  //  ... Many more
}

export interface RecommendedArticlesTeaserBlok extends SbEditableBlok {
  headline?: string
  headlineSyntax: Heading
  recommendedArticles: RecommendedArticleTeaser[]
}

export interface RecommendedArticleBlok extends SbEditableBlok {
  component: BlogComponentType
  lang: string
  postCategory?: string
  companyLogo?: SbAsset
  media?: SbAsset
  title: string
  useCaseIcon?: SbAsset
  //  ...Many more
}

export interface TranslatedSlug {
  name: string | null
  lang: string
  path: string
}

export enum BlogComponentType {
  PDF_KNOWLEDGE = 'PdfKnowledge',
  CUSTOMER_STORIES = 'CustomerStories',
  USE_CASE = 'UseCase',
  PRODUCT_NEWS = 'ProductNews',
  COMPANY_INSIGHTS = 'CompanyInsights',
}

export type TypoHeading = 'xl' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
export type Heading = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

export interface JWTData {
  id: string
  role: string
  exp: number
  iat: number
  nbf: number
}

export interface SbPageContext<T = object, u = string> extends SbEditableBlok {
  // TODO: lang needs to be changed on multilanguage got merged
  alternates: any[]
  content: T
  created_at: string
  default_full_slug: string
  field_component: u
  field_pageDescription_string: string
  field_pageTitle_string: string
  first_published_at: string
  full_slug: string
  id: string
  lang?: string
  name: string
  published_at: string
  slug: string
  translated_slugs: TranslatedSlug[]
  uuid: string
}

export interface BlogTestimonial {
  component?: string
  name?: string
  portraitImage?: SbAsset
  position?: string
  quote?: string
  _uid: string
}

export type ProductVariant = { content: { icon: string; name: string } }
